import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft, Phone, Mail, MapPin, Edit, Save, X,
  MessageSquare, FileText, Briefcase, Send, Camera, Bot, Plus
} from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";

const STAGES = [
  "new_lead", "contacted", "estimate_scheduled", "estimate_sent",
  "waiting_approval", "approved", "scheduled", "in_progress", "completed", "paid"
];
const FENCE_TYPES = ["wood", "cedar", "chain_link", "ornamental_iron", "vinyl"];

const STAGE_COLORS = {
  new_lead: "bg-blue-100 text-blue-700",
  contacted: "bg-violet-100 text-violet-700",
  estimate_scheduled: "bg-amber-100 text-amber-700",
  estimate_sent: "bg-orange-100 text-orange-700",
  waiting_approval: "bg-red-100 text-red-700",
  approved: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-cyan-100 text-cyan-700",
  in_progress: "bg-orange-100 text-orange-700",
  completed: "bg-green-100 text-green-700",
  paid: "bg-green-200 text-green-800",
};

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [comms, setComms] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [commType, setCommType] = useState("note");
  const [photos, setPhotos] = useState([]);

  const fetchLead = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      setEditData(data);
    } catch { toast.error("Lead not found"); navigate("/pipeline"); }
  }, [id, navigate]);

  const fetchComms = useCallback(async () => {
    try { const { data } = await api.get(`/communications/${id}`); setComms(data); } catch (err) { console.error("Failed to load communications:", err); }
  }, [id]);

  const fetchEstimates = useCallback(async () => {
    try { const { data } = await api.get(`/estimates?lead_id=${id}`); setEstimates(data); } catch (err) { console.error("Failed to load estimates:", err); }
  }, [id]);

  const fetchPhotos = useCallback(async () => {
    try { const { data } = await api.get(`/photos/lead/${id}`); setPhotos(data); } catch (err) { console.error("Failed to load photos:", err); }
  }, [id]);

  useEffect(() => { fetchLead(); fetchComms(); fetchEstimates(); fetchPhotos(); }, [fetchLead, fetchComms, fetchEstimates, fetchPhotos]);

  const handleSave = async () => {
    try {
      const { data } = await api.put(`/leads/${id}`, editData);
      setLead(data);
      setEditing(false);
      toast.success("Lead updated");
    } catch { toast.error("Failed to update"); }
  };

  const handleStageChange = async (stage) => {
    try {
      const { data } = await api.put(`/leads/${id}/stage`, { stage });
      setLead(data);
      toast.success("Stage updated");
    } catch { toast.error("Failed to update stage"); }
  };

  const handleAddComm = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await api.post("/communications", { lead_id: id, type: commType, content: newNote, direction: "outbound" });
      setNewNote("");
      fetchComms();
      if (commType === "sms" && data.sms_result?.success) {
        toast.success("SMS sent successfully!");
      } else if (commType === "sms" && data.sms_result && !data.sms_result.success) {
        toast.error(`SMS failed: ${data.sms_result.error || "Unknown error"}`);
        toast.info("Communication logged to timeline");
      } else {
        toast.success("Added to timeline");
      }
    } catch { toast.error("Failed to add"); }
  };

  if (!lead) return <div className="flex items-center justify-center h-64"><div className="text-zinc-400">Loading...</div></div>;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/pipeline")} data-testid="back-to-pipeline">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-zinc-900">{lead.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {lead.phone && <span className="flex items-center gap-1 text-sm text-zinc-500"><Phone className="w-3.5 h-3.5" /> {lead.phone}</span>}
            {lead.email && <span className="flex items-center gap-1 text-sm text-zinc-500"><Mail className="w-3.5 h-3.5" /> {lead.email}</span>}
            {lead.address && <span className="flex items-center gap-1 text-sm text-zinc-500"><MapPin className="w-3.5 h-3.5" /> {lead.address}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={lead.stage} onValueChange={handleStageChange}>
            <SelectTrigger className="w-48" data-testid="lead-stage-select">
              <Badge className={`${STAGE_COLORS[lead.stage]} capitalize`}>{lead.stage?.replace(/_/g, " ")}</Badge>
            </SelectTrigger>
            <SelectContent>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-lead-btn">
            {editing ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="w-full justify-start bg-zinc-100 border border-zinc-200">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="estimates">Estimates</TabsTrigger>
          <TabsTrigger value="photos" data-testid="photos-tab">Photos ({photos.length})</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="mt-4">
          <Card className="border border-zinc-200 shadow-none">
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: "Name", key: "name", type: "text" },
                  { label: "Phone", key: "phone", type: "text" },
                  { label: "Email", key: "email", type: "email" },
                  { label: "Address", key: "address", type: "text" },
                  { label: "Lead Source", key: "lead_source", type: "text" },
                  { label: "Fence Length (ft)", key: "fence_length", type: "number" },
                  { label: "Fence Height (ft)", key: "fence_height", type: "number" },
                  { label: "Gate Details", key: "gate_details", type: "text" },
                ].map((field) => (
                  <div key={field.key} className="space-y-1.5">
                    <Label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{field.label}</Label>
                    {editing ? (
                      <Input
                        type={field.type}
                        value={editData[field.key] || ""}
                        onChange={(e) => setEditData({ ...editData, [field.key]: field.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
                        className="border-zinc-300"
                        data-testid={`edit-${field.key}`}
                      />
                    ) : (
                      <p className="text-sm text-zinc-900">{lead[field.key] || "-"}</p>
                    )}
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Fence Type</Label>
                  {editing ? (
                    <Select value={editData.fence_type || ""} onValueChange={(v) => setEditData({ ...editData, fence_type: v })}>
                      <SelectTrigger data-testid="edit-fence-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {FENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-zinc-900 capitalize">{lead.fence_type?.replace(/_/g, " ") || "-"}</p>
                  )}
                </div>
                <div className="col-span-1 sm:col-span-2 space-y-1.5">
                  <Label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Project Notes</Label>
                  {editing ? (
                    <Textarea
                      value={editData.project_notes || ""}
                      onChange={(e) => setEditData({ ...editData, project_notes: e.target.value })}
                      rows={4}
                      data-testid="edit-project-notes"
                    />
                  ) : (
                    <p className="text-sm text-zinc-900 whitespace-pre-wrap">{lead.project_notes || "No notes"}</p>
                  )}
                </div>
              </div>
              {editing && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-100">
                  <Button variant="outline" onClick={() => { setEditing(false); setEditData(lead); }}>Cancel</Button>
                  <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-lead-btn">
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={() => navigate(`/estimates/new?lead_id=${id}`)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-estimate-btn">
              <FileText className="w-4 h-4 mr-2" /> Create Estimate
            </Button>
            <Button variant="outline" onClick={() => { handleStageChange("approved"); navigate(`/jobs?lead_id=${id}`); }} data-testid="convert-to-job-btn">
              <Briefcase className="w-4 h-4 mr-2" /> Convert to Job
            </Button>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-4">
          <Card className="border border-zinc-200 shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold">Communication Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add communication */}
              <div className="flex gap-2">
                <Select value={commType} onValueChange={setCommType}>
                  <SelectTrigger className="w-28" data-testid="comm-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder={commType === "sms" ? "Type SMS message to send to customer..." : "Add a note or log communication..."}
                  className="flex-1"
                  data-testid="comm-input"
                  onKeyDown={(e) => e.key === "Enter" && handleAddComm()}
                />
                <Button onClick={handleAddComm} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="add-comm-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <Separator />
              {comms.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-6">No communications yet</p>
              ) : (
                <div className="space-y-3">
                  {comms.map((c) => (
                    <div key={c.id} className={`flex gap-3 py-2 ${c.direction === "system" ? "bg-amber-50/50 -mx-2 px-2 rounded-lg border border-amber-100" : ""}`} data-testid={`comm-${c.id}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                        ${c.direction === "system" ? "bg-amber-100" : c.type === "call" ? "bg-blue-100" : c.type === "sms" ? "bg-green-100" : c.type === "email" ? "bg-purple-100" : "bg-zinc-100"}`}>
                        {c.direction === "system" ? <Bot className="w-3.5 h-3.5 text-amber-600" /> :
                         c.type === "call" ? <Phone className="w-3.5 h-3.5 text-blue-600" /> :
                         c.type === "sms" ? <MessageSquare className="w-3.5 h-3.5 text-green-600" /> :
                         c.type === "email" ? <Mail className="w-3.5 h-3.5 text-purple-600" /> :
                         <MessageSquare className="w-3.5 h-3.5 text-zinc-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {c.direction === "system" ? (
                            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">Auto Follow-Up</Badge>
                          ) : (
                            <>
                              <Badge variant="secondary" className="text-xs capitalize">{c.type}</Badge>
                              <span className="text-xs text-zinc-400">{c.direction}</span>
                            </>
                          )}
                          <span className="text-xs text-zinc-400 ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-zinc-700 mt-1">{c.content}</p>
                        {c.sms_result && (
                          <span className={`text-xs mt-0.5 ${c.sms_result.success ? "text-green-600" : "text-red-500"}`}>
                            {c.sms_result.success ? "SMS delivered" : `SMS failed: ${c.sms_result.error || "error"}`}
                          </span>
                        )}
                        {c.user_name && <p className="text-xs text-zinc-400 mt-0.5">by {c.user_name}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Estimates Tab */}
        <TabsContent value="estimates" className="mt-4">
          <Card className="border border-zinc-200 shadow-none">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold">Estimates</CardTitle>
                <Button size="sm" onClick={() => navigate(`/estimates/new?lead_id=${id}`)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="new-estimate-btn">
                  <Plus className="w-3 h-3 mr-1" /> New Estimate
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {estimates.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-6">No estimates yet</p>
              ) : (
                <div className="space-y-2">
                  {estimates.map((est) => (
                    <button
                      key={est.id}
                      onClick={() => navigate(`/estimates/${est.id}`)}
                      className="w-full flex items-center justify-between p-3 rounded-lg border border-zinc-100 hover:border-orange-300 transition-colors text-left"
                      data-testid={`estimate-${est.id}`}
                    >
                      <div>
                        <p className="text-sm font-semibold text-zinc-900">{est.fence_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())} - {est.linear_footage}ft</p>
                        <p className="text-xs text-zinc-500">{new Date(est.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-zinc-900">${(est.total || 0).toLocaleString()}</p>
                        <Badge variant="secondary" className={`text-xs capitalize ${est.status === "approved" ? "bg-green-100 text-green-700" : est.status === "sent" ? "bg-orange-100 text-orange-700" : "bg-zinc-100 text-zinc-600"}`}>
                          {est.status}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="mt-4">
          <Card className="border border-zinc-200 shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Camera className="w-5 h-5 text-orange-600" /> Property Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PhotoUpload
                entityType="lead"
                entityId={id}
                photoType="property"
                photos={photos}
                onPhotosChange={fetchPhotos}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
